import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const MeetingContext = createContext({
  activeRoomId: null,
  isMinimized: false,
  openMeeting: () => {},
  minimizeMeeting: () => {},
  maximizeMeeting: () => {},
  closeMeeting: () => {}
});

export function MeetingProvider({ children }) {
  const [activeRoomId, setActiveRoomId] = useState(() => {
    if (typeof window !== 'undefined') {
      const match = window.location.pathname.match(/\/meetings\/room\/([^/]+)/);
      if (match && match[1]) return match[1];
      return sessionStorage.getItem('activeMeetingRoomId') || null;
    }
    return null;
  });

  const [isMinimized, setIsMinimized] = useState(() => {
    if (typeof window !== 'undefined') {
      if (window.location.pathname.includes('/meetings/room/')) {
        return false;
      }
      return Boolean(sessionStorage.getItem('activeMeetingRoomId'));
    }
    return false;
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Sync with URL: entering /meetings/room/:roomId maximizes, navigating away minimizes to PiP, or clears if left
  useEffect(() => {
    const match = location.pathname.match(/\/meetings\/room\/([^/]+)/);
    if (match && match[1]) {
      const targetRoom = match[1];
      setActiveRoomId(targetRoom);
      setIsMinimized(false);
      sessionStorage.setItem('activeMeetingRoomId', targetRoom);
    } else {
      const saved = sessionStorage.getItem('activeMeetingRoomId');
      if (saved) {
        setActiveRoomId(saved);
        setIsMinimized(true);
      } else {
        setActiveRoomId(null);
        setIsMinimized(false);
      }
    }
  }, [location.pathname]);

  const openMeeting = useCallback((roomId) => {
    setActiveRoomId(roomId);
    setIsMinimized(false);
    sessionStorage.setItem('activeMeetingRoomId', roomId);
  }, []);

  const minimizeMeeting = useCallback(() => {
    setIsMinimized(true);
    // If currently on the room URL, navigate to default dashboard so user can use HRMS sidebar menus
    if (location.pathname.includes('/meetings/room/')) {
      navigate('/dashboard/default');
    }
  }, [location.pathname, navigate]);

  const maximizeMeeting = useCallback(() => {
    setIsMinimized(false);
    if (activeRoomId) {
      navigate(`/meetings/room/${activeRoomId}`);
    }
  }, [activeRoomId, navigate]);

  const closeMeeting = useCallback(() => {
    setActiveRoomId(null);
    setIsMinimized(false);
    sessionStorage.removeItem('activeMeetingRoomId');
  }, []);

  return (
    <MeetingContext.Provider
      value={{
        activeRoomId,
        isMinimized,
        openMeeting,
        minimizeMeeting,
        maximizeMeeting,
        closeMeeting
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
}

export function useMeeting() {
  return useContext(MeetingContext);
}

export default MeetingContext;
